import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
} from "recharts";
import { TrendingUp, Warning, TrendingDown } from "@mui/icons-material";

const convertToChartData = (data) => {
  const initialData = data.map((item) => ({
    name: item.academicYear,
    Vacancies: item.count,
    Total: item.count,
  }));

  const firstYearLabel = initialData.length > 0 ? initialData[0].name : "";
  
  return initialData.map((item, index) => {
    if (index === 0) {
      return {
        ...item,
        growthRate: null, // No growth rate for baseline year
        yearOverYearChange: null,
        annotation: "Baseline Year",
        firstYearLabel,
      };
    }

    const previousTotal = initialData[index - 1].Total;
    const yearOverYearChange = item.Total - previousTotal;

    const isPeakYear =
      item.Total === Math.max(...initialData.map((d) => d.Total));
    const isCurrentYear = index === initialData.length - 1;

    let annotation = null;
    if (
      index === initialData.length - 2 &&
      isCurrentYear === false &&
      isPeakYear
    ) {
      annotation = "Peak vacancies";
    } else if (isCurrentYear) {
      annotation = "Current year";
    } else if (
      yearOverYearChange ===
      Math.max(
        ...initialData.slice(1).map((d, i) => {
          const prevTotal = initialData[i].Total;
          return d.Total - prevTotal;
        })
      )
    ) {
      annotation = "Highest year-over-year growth";
    }

    return {
      ...item,
      growthRate: item.Total, 
      yearOverYearChange,
      annotation,
      firstYearLabel,
    };
  });
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;

    return (
      <div
        style={{
          backgroundColor: "white",
          padding: "12px",
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
          boxShadow: "0 3px 8px rgba(0,0,0,0.15)",
        }}
      >
        <h5
          style={{
            fontWeight: 600,
            marginTop: 0,
            marginBottom: "8px",
            color: "#333",
            borderBottom: "1px solid #eee",
            paddingBottom: "6px",
          }}
        >
          Academic Year {label}
        </h5>
        <div style={{ fontSize: "13px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "6px",
            }}
          >
            <span style={{ color: "#666", marginRight: "24px" }}>
              Vacancies:
            </span>
            <span style={{ fontWeight: 600, color: "#015198" }}>
              {data.Vacancies?.toLocaleString()}
            </span>
          </div>

          {data.name !== data.firstYearLabel && data.yearOverYearChange !== null && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "6px",
              }}
            >
              <span style={{ color: "#666", marginRight: "24px" }}>
                Change from Previous Year:
              </span>
              <span
                style={{
                  fontWeight: 600,
                  color: data.yearOverYearChange > 0 ? "#2e7d32" : "#d32f2f", 
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {data.yearOverYearChange > 0 ? (
                  <TrendingUp style={{ fontSize: 14, marginRight: "4px" }} />
                ) : (
                  <TrendingDown style={{ fontSize: 14, marginRight: "4px" }} />
                )}
                {data.yearOverYearChange > 0 ? "+" : ""}
                {data.yearOverYearChange?.toLocaleString()}
              </span>
            </div>
          )}

          {data.annotation && (
            <div
              style={{
                marginTop: "8px",
                paddingTop: "6px",
                borderTop: "1px solid #eee",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Warning
                style={{
                  height: "14px",
                  width: "14px",
                  marginRight: "6px",
                  color: "#ed6c02",
                }}
              />
              <span style={{ color: "#ed6c02", fontWeight: 500 }}>
                {data.annotation}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }
  return null;
};

export default function AcademicYearVacancyDistributionChart({ data }) {
  const chartData = convertToChartData(data);

  return (
    <>
      <ResponsiveContainer width="100%" height={497}>
        <ComposedChart
          data={chartData}
          margin={{ top: 10, right: 10, left: 5, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis yAxisId="left" />
          <YAxis
            yAxisId="right"
            orientation="right"
            hide={false}
            domain={['dataMin', 'dataMax']}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar
            yAxisId="left"
            dataKey="Vacancies"
            fill="hsl(var(--chart-1))"
            name="Vacancies"
          >
            <LabelList
              dataKey="Vacancies"
              position="inside"
              formatter={(value) => value.toLocaleString()}
              style={{ fill: "white", fontWeight: "bold" }}
            />
          </Bar>
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="growthRate"
            name="Year-over-Year Change"
            stroke="hsl(var(--chart-4))"
            strokeWidth={2}
            dot={{ r: 4, strokeWidth: 2, fill: "white" }}
            activeDot={{ r: 6, strokeWidth: 2, stroke: "white" }}
            connectNulls={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            fontSize: "13px",
            color: "#666",
            display: "flex",
            alignItems: "center",
          }}
        >
          <span
            style={{
              height: "10px",
              width: "10px",
              backgroundColor: "hsl(var(--chart-1))",
              display: "inline-block",
              marginRight: "5px",
              borderRadius: "2px",
            }}
          ></span>
          <span style={{ marginRight: "15px" }}>Vacancies</span>

          <span
            style={{
              height: "10px",
              width: "10px",
              backgroundColor: "hsl(var(--chart-4))",
              display: "inline-block",
              marginRight: "5px",
              borderRadius: "2px",
            }}
          ></span>
          <span>Year-over-Year Change</span>
        </div>
      </div>
      <div style={{ overflowX: "auto", marginTop: "20px" }}>
        <table
          style={{
            borderCollapse: "collapse",
            width: "max-content",
            minWidth: "100%",
          }}
        >
          <thead>
            <tr style={{ backgroundColor: "#f9f9f9" }}>
              <th
                style={{
                  border: "1px solid #ddd",
                  padding: "10px",
                  textAlign: "left",
                  position: "sticky",
                  left: 0,
                  backgroundColor: "white",
                  borderTop: "none",
                  borderLeft: "none",
                  zIndex: 1,
                }}
              ></th>
              {data.map((item, index) => (
                <th
                  key={index}
                  style={{
                    border: "1px solid #ddd",
                    padding: "10px",
                    textAlign: "center",
                    minWidth: "100px",
                  }}
                >
                  {item.academicYear}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr style={{ backgroundColor: "#fff" }}>
              <td
                style={{
                  border: "1px solid #ddd",
                  padding: "10px",
                  fontWeight: "bold",
                  position: "sticky",
                  left: 0,
                  backgroundColor: "#fff",
                  zIndex: 1,
                }}
              >
                Vacancies
              </td>
              {data.map((item, index) => (
                <td
                  key={index}
                  style={{
                    border: "1px solid #ddd",
                    padding: "10px",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  {item.count?.toLocaleString()}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}
